import { useMemo } from 'react';
import {
  Activity,
  CircleDollarSign,
  Fuel,
  Truck,
  Users,
  Wallet,
} from 'lucide-react';
import { TRANSPORT_VENDORS } from './mockTransportVendorData';

const P = {
  silver: '#F1F5F9',
  white: '#FFFFFF',
  grid: '#E2E8F0',
  blue: '#3B82F6',
  blueTint: '#EFF6FF',
  text: '#1E293B',
  muted: '#64748B',
};

function OverviewCard({ icon: Icon, label, value, hint, accent = P.blue }) {
  return (
    <div
      className="rounded-2xl border p-5 shadow-sm"
      style={{
        backgroundColor: P.white,
        borderColor: P.grid,
        borderWidth: 1,
        borderStyle: 'solid',
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div
          className="flex h-11 w-11 items-center justify-center rounded-full"
          style={{ backgroundColor: P.blueTint }}
        >
          <Icon className="h-5 w-5" style={{ color: accent }} strokeWidth={1.75} />
        </div>
      </div>
      <p
        className="mt-4 text-[10px] font-bold uppercase tracking-widest"
        style={{ color: P.muted }}
      >
        {label}
      </p>
      <p className="mt-1 font-mono text-2xl font-black tabular-nums" style={{ color: P.text }}>
        {value}
      </p>
      {hint && (
        <p className="mt-1 text-xs font-medium" style={{ color: P.muted }}>
          {hint}
        </p>
      )}
    </div>
  );
}

export default function DashboardOverview() {
  const stats = useMemo(() => {
    const activeVendors = TRANSPORT_VENDORS.filter((v) => v.contractStatus === 'Active').length;
    const tankers = TRANSPORT_VENDORS.reduce(
      (sum, v) => (v.contractStatus === 'Active' ? sum + v.totalFleets : sum),
      0,
    );
    const pendingPayouts = TRANSPORT_VENDORS.reduce((sum, v) => sum + v.pendingInvoices, 0);
    return { activeVendors, tankers, pendingPayouts, payrollDrivers: 10 };
  }, []);

  return (
    <div
      className="rounded-2xl border p-6 sm:p-8"
      style={{
        backgroundColor: P.silver,
        borderColor: P.grid,
        color: P.text,
        colorScheme: 'light',
      }}
    >
      <header className="mb-8">
        <span
          className="inline-block rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-widest"
          style={{
            backgroundColor: P.white,
            borderColor: P.grid,
            color: P.blue,
          }}
        >
          Mission Control · Live Pulse
        </span>
        <h1 className="mt-2 text-2xl font-black tracking-tight sm:text-3xl" style={{ color: P.text }}>
          Dashboard Overview
        </h1>
        <p className="mt-1 text-xs font-bold uppercase tracking-wider" style={{ color: P.muted }}>
          Fast-scan logistics KPIs · UAE fuel transport network
        </p>
      </header>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <OverviewCard
          icon={Activity}
          label="Dispatch Readiness"
          value="94%"
          hint="Fleet available for same-day routing"
        />
        <OverviewCard
          icon={Wallet}
          label="Payroll Drivers Synced"
          value={stats.payrollDrivers}
          hint="June 2026 automated ledger"
        />
        <OverviewCard
          icon={Truck}
          label="Outsourced Tankers"
          value={stats.tankers}
          hint={`${stats.activeVendors} active vendor partners`}
        />
        <OverviewCard
          icon={CircleDollarSign}
          label="Pending Vendor Payouts"
          value={`${stats.pendingPayouts.toLocaleString()} AED`}
          hint="Awaiting finance clearance"
        />
      </div>

      <div
        className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-2"
      >
        <section
          className="rounded-2xl border p-5"
          style={{ backgroundColor: P.white, borderColor: P.grid }}
        >
          <div className="mb-4 flex items-center gap-2">
            <Users className="h-5 w-5" style={{ color: P.blue }} />
            <h2 className="text-sm font-extrabold" style={{ color: P.text }}>
              Human Capital Snapshot
            </h2>
          </div>
          <ul className="space-y-3 text-sm font-semibold" style={{ color: P.muted }}>
            <li className="flex justify-between border-b pb-2" style={{ borderColor: P.grid }}>
              <span>Active driver contracts</span>
              <span className="font-mono font-bold" style={{ color: P.text }}>
                {stats.payrollDrivers}
              </span>
            </li>
            <li className="flex justify-between border-b pb-2" style={{ borderColor: P.grid }}>
              <span>Automated trip incentives</span>
              <span className="font-mono font-bold" style={{ color: P.text }}>
                75 AED / drop
              </span>
            </li>
            <li className="flex justify-between">
              <span>OT baseline multiplier</span>
              <span className="font-mono font-bold" style={{ color: P.text }}>
                1.5× hourly
              </span>
            </li>
          </ul>
        </section>

        <section
          className="rounded-2xl border p-5"
          style={{ backgroundColor: P.white, borderColor: P.grid }}
        >
          <div className="mb-4 flex items-center gap-2">
            <Fuel className="h-5 w-5" style={{ color: P.blue }} />
            <h2 className="text-sm font-extrabold" style={{ color: P.text }}>
              Vendor Network Snapshot
            </h2>
          </div>
          <ul className="space-y-3 text-sm font-semibold" style={{ color: P.muted }}>
            <li className="flex justify-between border-b pb-2" style={{ borderColor: P.grid }}>
              <span>Registered transport vendors</span>
              <span className="font-mono font-bold" style={{ color: P.text }}>
                {TRANSPORT_VENDORS.length}
              </span>
            </li>
            <li className="flex justify-between border-b pb-2" style={{ borderColor: P.grid }}>
              <span>Active contract partners</span>
              <span className="font-mono font-bold" style={{ color: P.text }}>
                {stats.activeVendors}
              </span>
            </li>
            <li className="flex justify-between">
              <span>Under review / expired</span>
              <span className="font-mono font-bold" style={{ color: '#D97706' }}>
                {TRANSPORT_VENDORS.length - stats.activeVendors}
              </span>
            </li>
          </ul>
        </section>
      </div>
    </div>
  );
}
